import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Switch } from '@mui/material';
import { createInvestorViewRoute } from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import ButtonGroup from 'components/ButtonGroup';
import Heading from 'components/Heading';
import { Row } from 'components/Layout';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageHeader from 'components/PageHeader';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import RecordView from 'components/RecordView';
import Spacing from 'components/Spacing';
import { AccountType, Schema } from 'further-types/investor';
import { useDisclosure } from 'further-ui/hooks';
import { Country } from 'further-ui/labels';
import { CalendarDay } from 'further-ui/utils';
import { isValid } from 'date-fns';
import {
  InvestorFormValues,
  getInitialValues,
  createConditionalInvestorSchema,
} from 'helpers/investor/investorForm';
import { useInvestor } from 'hooks/data/investor/useInvestor';
import useSetBankDetails from 'hooks/data/investor/useSetBankDetails';
import { useGetRole } from 'hooks/ui/useGetRole';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import InvestorDetails from './InvestorDetails';
import TaxAndBankDetail from './TaxAndBankDetail';
import api from 'lib/trpcClient';
import FieldRow from 'components/FormElements/FieldRow';
import { FirmSelector } from 'components/FirmSelector';

const UpdateInvestor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { isSuperAdmin, firmId: usersFirmId } = useGetRole();
  const history = useHistory();
  const nameChangeDisclosure = useDisclosure();
  const { state } = useLocation<{ returnTo: string }>();
  const queryClient = useQueryClient();

  const {
    updateInvestorAsync,
    createInvestorAsync,
    createLegalEntityAsync,
    isCreatingInvestor,
    isUpdatingInvestor,
  } = useInvestor(id);
  const setBankDetails = useSetBankDetails();

  const { data: investorDetails } = api.investor.getInvestor.useQuery(
    { investorId: id ?? '' },
    { enabled: !!id },
  );

  const initialValues = getInitialValues(investorDetails, usersFirmId);

  // Create conditional schema based on edit mode and existing data
  const conditionalSchema = createConditionalInvestorSchema(
    !!id,
    investorDetails,
  );

  const methods = useForm<InvestorFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(conditionalSchema),
    criteriaMode: 'all',
    //@ts-expect-error
    defaultValues: initialValues,
    //@ts-expect-error
    values: id ? initialValues : undefined,
  });

  const { control, handleSubmit, watch } = methods;
  const accountType = watch('accountType');

  const getSubmitHandler =
    ({ disableAllEmails = null, ignoreNameChange = null } = {}) =>
    (values: InvestorFormValues) => {
      const identificationDetailsChanged =
        (values.accountType === AccountType.Individual &&
          (investorDetails?.firstName !== values.firstName ||
            investorDetails?.lastName !== values.lastName ||
            investorDetails?.middleName !== values.middleName)) ||
        (values.accountType === AccountType.LegalEntity &&
          values.registrationNumber !== investorDetails?.registrationNumber);

      if (id && ignoreNameChange === null && identificationDetailsChanged) {
        nameChangeDisclosure.onOpen();
        return;
      }

      const request = {
        ...values,
        dob:
          values.accountType === AccountType.Individual && values.dob
            ? new CalendarDay(values.dob)
            : undefined,
        registrationDate:
          values.accountType === AccountType.LegalEntity &&
          values.registrationDate
            ? new CalendarDay(values.registrationDate)
            : undefined,
        dateOfDeath:
          values.dateOfDeath && isValid(values.dateOfDeath)
            ? new CalendarDay(values.dateOfDeath)
            : undefined,
      };

      const submit = async () => {
        try {
          const requireCountry = (country: Country | '') => {
            if (country === '') {
              throw new Error('Please select country.');
            }
            return country;
          };

          const toApiAddress = (
            address: InvestorFormValues['address'][number],
          ) => ({
            ...address,
            country: requireCountry(address.country),
          });

          if (id) {
            const normalizedIncomeSources =
              values.accountType === AccountType.Individual
                ? values.incomeSources
                    ?.map((source) => source.value)
                    .filter((source) => source !== '') ?? []
                : [];

            const { dateRegister: _dateRegister, ...valuesForUpdate } = values;

            const updateRequest = {
              ...valuesForUpdate,
              type: 'admin',
              signUpSource: Schema.SignUpSource.AddSingleInvestor,
              ignoreNameChange: ignoreNameChange ?? undefined,
              defaultAdviserId: values.defaultAdviserId || undefined,
              customTags: values.customTags ?? undefined,
              title: 'title' in values ? values.title ?? undefined : undefined,
              dob:
                values.accountType === AccountType.Individual &&
                values.dob &&
                isValid(values.dob)
                  ? new CalendarDay(values.dob).toString()
                  : undefined,
              registrationDate:
                values.accountType === AccountType.LegalEntity &&
                values.registrationDate &&
                isValid(values.registrationDate)
                  ? new CalendarDay(values.registrationDate).toString()
                  : undefined,
              dateOfDeath:
                values.dateOfDeath && isValid(values.dateOfDeath)
                  ? new CalendarDay(values.dateOfDeath).toString()
                  : undefined,
              incomeSources:
                values.accountType === AccountType.Individual
                  ? normalizedIncomeSources
                  : undefined,
            };

            await updateInvestorAsync(updateRequest);

            setBankDetails.mutate({ investorId: id, ...values });

            await queryClient.invalidateQueries({
              queryKey: ['investor-certification', id],
            });

            history.push(state?.returnTo || createInvestorViewRoute(id));
            return;
          }

          const commonCreateInput = {
            signUpSource: Schema.SignUpSource.AddSingleInvestor,
            address: request.address.map(toApiAddress),
            ...(disableAllEmails !== null ? { disableAllEmails } : {}),
            customTags: request.customTags ?? undefined,
            defaultAdviserId: request.defaultAdviserId || undefined,
          };

          let created: { _id: string };
          if (request.accountType === AccountType.LegalEntity) {
            const registrationDate = request.registrationDate;
            if (!registrationDate) {
              throw new Error('Registration date is required');
            }

            const legalEntityInput = {
              ...request,
              ...commonCreateInput,
              registrationDate,
            };

            created = await createLegalEntityAsync(legalEntityInput);
          } else {
            const dob = request.dob;
            if (!dob) {
              throw new Error('Date of birth is required');
            }

            const { incomeSources, ...investorRequest } = request;

            const normalizedIncomeSources = incomeSources
              ?.map((source) => source.value)
              .filter((source) => source !== '');

            const investorInput = {
              ...investorRequest,
              ...commonCreateInput,
              dob,
              incomeSources: normalizedIncomeSources?.length
                ? normalizedIncomeSources
                : undefined,
              title: request.title ?? undefined,
            };

            created = await createInvestorAsync(investorInput);
          }

          const createdInvestorId = created._id;

          setBankDetails.mutate({ investorId: createdInvestorId, ...values });

          await queryClient.invalidateQueries({
            queryKey: ['investor-certification', createdInvestorId],
          });

          if (state?.returnTo) {
            history.push(state.returnTo);
          } else {
            history.push(createInvestorViewRoute(createdInvestorId));
          }
        } catch (error) {
          if (
            error instanceof Error &&
            (error.message.includes('required') ||
              error.message.includes('Please select country.'))
          ) {
            return;
          }
          captureException(error);
        } finally {
          nameChangeDisclosure.onClose();
        }
      };

      void submit();
    };

  const disableSubmit = isCreatingInvestor || isUpdatingInvestor;
  const isLegalEntity = accountType === AccountType.LegalEntity;

  return (
    <PageContainer heading={`Investor: ${id ? 'Edit' : 'Add'} Investor`}>
      <PageContent>
        <FormProvider {...methods}>
          <form onSubmit={(e) => e.preventDefault()}>
            <Spacing>
              <PageHeader>
                <Heading variant="h2" noMargin>
                  Investor details
                </Heading>
                <ResponsiveActions>
                  {id ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => history.push(createInvestorViewRoute(id))}
                    >
                      View Investor
                    </Button>
                  ) : (
                    <Controller
                      name="accountType"
                      control={control}
                      render={({
                        field: { ref, value, onChange, ...rest },
                      }) => (
                        <Row centered spacing="xs">
                          <span>Individual</span>
                          <Switch
                            {...rest}
                            onChange={(e) => {
                              onChange(
                                e.target.checked
                                  ? AccountType.LegalEntity
                                  : AccountType.Individual,
                              );
                            }}
                            checked={value === AccountType.LegalEntity}
                            inputProps={{ 'aria-label': 'Switch' }}
                          />
                          <span>Legal entity</span>
                        </Row>
                      )}
                    />
                  )}
                </ResponsiveActions>
              </PageHeader>

              <RecordView>
                {isSuperAdmin && (
                  <FieldRow title="Firm*" centerTitle>
                    {id ? (
                      investorDetails?.firmName
                    ) : (
                      <Controller
                        name="firmId"
                        control={control}
                        render={({ field }) => (
                          <FirmSelector
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                            firmId={field.value}
                          />
                        )}
                      />
                    )}
                  </FieldRow>
                )}
                <InvestorDetails
                  id={id}
                  accountType={accountType}
                  emailInUseByOtherAccount={
                    investorDetails?.emailInUseByOtherAccount ?? false
                  }
                />
                <TaxAndBankDetail control={control} />
                <ButtonGroup>
                  {id ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSubmit(getSubmitHandler())()}
                      type="submit"
                      disabled={disableSubmit}
                    >
                      Update
                    </Button>
                  ) : (
                    <>
                      {!isSuperAdmin && (
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={disableSubmit}
                          onClick={() =>
                            handleSubmit(
                              //@ts-expect-error
                              getSubmitHandler({ disableAllEmails: false }),
                            )()
                          }
                        >
                          Add and invite investor
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={disableSubmit}
                        onClick={() =>
                          handleSubmit(
                            //@ts-expect-error
                            getSubmitHandler({ disableAllEmails: true }),
                          )()
                        }
                      >
                        {isSuperAdmin
                          ? 'Add investor'
                          : 'Add investor with emails disabled'}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => history.push('/investor')}
                    disabled={disableSubmit}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </RecordView>
            </Spacing>
          </form>
        </FormProvider>
      </PageContent>
      <AlertDialog
        open={nameChangeDisclosure.isOpen}
        onClose={() =>
          //@ts-expect-error
          handleSubmit(getSubmitHandler({ ignoreNameChange: false }))()
        }
        onConfirm={() =>
          //@ts-expect-error
          handleSubmit(getSubmitHandler({ ignoreNameChange: true }))()
        }
        title="Warning"
        content={
          isLegalEntity ? (
            <p>
              You have changed this investor's registration number. Do you want
              to reset the investor's KYB as well? If so, the investor will be
              asked to re-complete KYB when they create their next
              subscription.. Old KYB information will remain on their account.
            </p>
          ) : (
            <p>
              You have changed this investor's name. Do you want to reset the
              investor's KYC as well? If so, the investor will be asked to
              re-complete KYC when they make their next subscription. Old KYC
              information will remain on their account.
            </p>
          )
        }
        btnLabels={{
          cancel: isLegalEntity ? 'Yes, reset KYB ' : 'Yes, reset KYC',
          confirm: isLegalEntity ? 'No, maintain KYB' : 'No, maintain KYC',
        }}
      />
    </PageContainer>
  );
};
export default UpdateInvestor;
